import type { DeliveryCard, DispatchCard, RouteAnchor } from 'src/entities/dispatch';

/**
 * ほかの行へ移した店舗を、元の行のルートの位置に薄く残すカード（PM フィードバック 2026-10-27）。
 * 本日なし（no-delivery）として持つので、ドラッグ不可・集計外・1 つ前の地点の直後に置く扱いは本日なしと同じ。
 * 移動先の表記は行を組み立てたあと applyMovedOutLabels が付ける。
 */

export const isMovedOutCard = (card: DispatchCard): card is DeliveryCard => card.type === 'delivery'
  && card.status === 'no-delivery'
  && (card.movedOutLegRunIds?.length ?? 0) > 0;

/** 今日の配車の降地カード（D&D で置いた template も）。本日なし・未反映は数えない */
export const isTodayDeliveryCard = (card: DispatchCard): card is DeliveryCard => card.type === 'delivery'
  && card.status !== 'no-delivery'
  && card.status !== 'fr-template';

const storeNamesOf = (card: DeliveryCard): string[] => card.anchorNames ?? [card.storeName];

/**
 * D&D で行から外したカードのうち、行のルートにある店舗を元の位置に残す。
 * 同じ店舗のカードがまだ行にあるとき・移した案件の id が分からないときは残さない（移動先を引けない）。
 *
 * Why not 表示の直前に足す: 次のコマンドの targetIndex は表示中の並びで数えるので、持つ並びに入れる（本日なしと同じ）
 */
export function addMovedOutCards(
  cards: DispatchCard[],
  removed: readonly DispatchCard[],
  rowId: string,
  routeStops: readonly RouteAnchor[] | undefined,
  unloadingNameOf: (legRunId: number) => string | null | undefined,
): DispatchCard[] {
  if (!routeStops || routeStops.length === 0) return cards;
  const remainingNames = new Set(cards.filter(isTodayDeliveryCard).flatMap(storeNamesOf));
  const added: DeliveryCard[] = [];
  for (const card of removed) {
    if (!isTodayDeliveryCard(card)) continue;
    for (const name of storeNamesOf(card)) {
      const stopIndex = routeStops.findIndex((s) => s.stopType === 'unloading' && s.placeName === name);
      if (stopIndex < 0 || remainingNames.has(name)) continue;
      const legRunIds = (card.legRunIds ?? []).filter((id) => (unloadingNameOf(id) ?? card.storeName) === name);
      if (legRunIds.length === 0) continue;
      const id = `moved-out-${rowId}-${name}`;
      const existing = added.find((c) => c.id === id);
      if (existing) {
        existing.movedOutLegRunIds = [...new Set([...(existing.movedOutLegRunIds ?? []), ...legRunIds])];
        continue;
      }
      added.push({
        id,
        type: 'delivery',
        storeName: name,
        storeAddress: card.storeAddress,
        cases: 0,
        todayCases: null,
        status: 'no-delivery',
        isActive: true,
        placeKey: id,
        frStopType: 'unloading',
        routeAnchors: routeStops.slice(0, stopIndex).reverse(),
        movedOutLegRunIds: legRunIds,
      });
    }
  }
  return added.length === 0 ? cards : [...cards, ...added];
}

/**
 * 同じ店舗のカードが行に載ったら（移した案件が戻った・同じ店舗の別の案件を置いた）、残したカードを消す。
 * Why not 移した案件の id だけで見る: 読み直すと行 VM は店舗名で決める（今日のカードに店舗があれば残さない）ので、表示が食い違う
 */
export function dropReturnedMovedOutCards(cards: DispatchCard[]): DispatchCard[] {
  if (!cards.some(isMovedOutCard)) return cards;
  const today = cards.filter(isTodayDeliveryCard);
  const names = new Set(today.flatMap(storeNamesOf));
  const ids = new Set(today.flatMap((c) => c.legRunIds ?? []));
  const next = cards.filter((c) => !isMovedOutCard(c)
    || !(names.has(c.storeName) || (c.movedOutLegRunIds ?? []).some((id) => ids.has(id))));
  return next.length === cards.length ? cards : next;
}
